import React, { useRef, useMemo } from "react";
import {
  EmptyStates,
  getBreakPoint,
  makeArrayFromLength,
  SkeletonLoader,
  useScreenWidth,
  useContainerQuery,
} from "oolib";
import { Fragment } from "react";
import ErrorStates from "../../../../ErrorStates";
import { VirtualizedList } from "@/components/UI/VirtualizedList";

/**
 * Calculate number of columns based on screen width and column settings.
 * @param {Object} colSetting - Column breakpoint settings
 * @param {number} screenWidth - Current screen width
 * @returns {number} Number of columns
 */
const calculateColumns = (colSetting, screenWidth) => {
  const keys = Object.keys(colSetting);
  const values = Object.values(colSetting);

  if (screenWidth < getBreakPoint(keys[0])) {
    return values[0];
  }

  if (keys[1]) {
    const [minBp, maxBp] = keys[1].split('_');
    if (screenWidth >= getBreakPoint(minBp) && screenWidth < getBreakPoint(maxBp)) {
      return values[1];
    }
  }

  if (keys[2] && screenWidth >= getBreakPoint(keys[2])) {
    return values[2];
  }

  return values[0];
};

/**
 * MasonryCardGridShell - A responsive masonry grid layout component.
 *
 * Supports both virtualized and non-virtualized rendering modes.
 * When virtualization is enabled, only visible items are rendered for better performance.
 *
 * Column calculation priority:
 * 1. forceCol - If provided, always use this exact column count
 * 2. Container-based (minCardWidth provided) - Uses ResizeObserver on actual container width
 * 3. Viewport-based (colSetting breakpoints) - Uses screen width breakpoints
 */
export const MasonryCardGridShell = ({
  dataFetchStatus,
  dataFetchError,
  isFetchingNextPage,
  data,
  renderCard,
  forceCol,
  colSetting = {
    md: 1,
    md_xl: 2,
    xl: 3,
  },
  minCardWidth,
  maxCardWidth,
  style,
  emptyStatePreset,
  enableVirtualization = false,
  useWindowScroll = false,
  scrollContainerRef: externalScrollRef,
  scrollContainerHeight,
  estimateSize = 350,
  overscan = 3,
}) => {
  const screenWidth = useScreenWidth();
  const internalScrollRef = useRef(null);

  // Use external ref if provided, otherwise use internal ref for self-contained scroll
  const scrollContainerRef = externalScrollRef || internalScrollRef;
  const useInternalScrollContainer = enableVirtualization && !externalScrollRef && !useWindowScroll;

  // Container-based responsive columns (measures actual container width)
  const useContainerMode = !forceCol && minCardWidth;
  const { ref: containerRef, containerWidth } = useContainerQuery({ enabled: useContainerMode });
  
  const containerCols = useMemo(() => {
    if (containerWidth === 0) return 1;
    const minW = minCardWidth || 280;
    let columns = Math.floor(containerWidth / minW);
    columns = Math.max(1, columns);
    if (maxCardWidth && containerWidth / columns > maxCardWidth) {
      columns = Math.ceil(containerWidth / maxCardWidth);
    }
    return columns;
  }, [containerWidth, minCardWidth, maxCardWidth]);

  // Viewport-based columns (uses screen width breakpoints)
  const viewportCols = calculateColumns(colSetting, screenWidth);

  // Column priority: forceCol > container-based > viewport-based
  const cols = forceCol ? forceCol : useContainerMode ? containerCols : viewportCols;

  // Adapter function to convert VirtualizedList's renderItem to existing renderCard signature
  const renderItemAdapter = (item, originalIndex) => {
    const colI = originalIndex % cols;
    const isLastCard = originalIndex === data.length - 1;
    return renderCard(cols, colI, item, originalIndex, [item], isLastCard);
  };

  // Loading state
  if (dataFetchStatus === "loading") {
    return (
      <div
        ref={useContainerMode ? containerRef : undefined}
        style={{
          gap: "1rem",
          display: "grid",
          gridTemplateColumns: makeArrayFromLength(cols)
            .map(() => "minmax(0, 100%)")
            .join(" "),
          ...style,
        }}
      >
        {makeArrayFromLength(20).map((d, i) => (
          <SkeletonLoader key={i} style={{ height: "300px", borderRadius: "0.5rem" }} />
        ))}
      </div>
    );
  }

  // Error state
  if (dataFetchStatus === "error") {
    return <ErrorStates errorResponseObject={dataFetchError} />;
  }

  // Empty state
  if (!data || data.length === 0) {
    if (emptyStatePreset) {
      return (
        <div style={{ gridColumn: `1/${cols + 1}` }}>
          <EmptyStates preset={emptyStatePreset} />
        </div>
      );
    }
    return <div />;
  }

  // Virtualized rendering
  if (enableVirtualization) {
    const virtualizedContent = (
      <>
        <VirtualizedList
          data={data}
          renderItem={renderItemAdapter}
          scrollContainerRef={useWindowScroll ? undefined : scrollContainerRef}
          useWindowScroll={useWindowScroll}
          columns={cols}
          gap={16}
          estimateSize={estimateSize}
          overscan={overscan}
          getItemKey={(item, index) => item.id || item._id || index}
        />
        {isFetchingNextPage && (
          <div
            style={{
              gap: "1rem",
              display: "grid",
              gridTemplateColumns: makeArrayFromLength(cols)
                .map(() => "minmax(0, 100%)")
                .join(" "),
              marginTop: "1rem",
            }}
          >
            {makeArrayFromLength(cols * 2).map((d, i) => (
              <SkeletonLoader key={i} style={{ height: "300px", borderRadius: "0.5rem" }} />
            ))}
          </div>
        )}
      </>
    );

    // If using internal scroll container, wrap content
    if (useInternalScrollContainer) {
      return (
        <div
          ref={(el) => {
            internalScrollRef.current = el;
            // Also attach containerRef for container-based column measurement
            if (useContainerMode && containerRef) containerRef(el);
          }}
          style={{
            height: scrollContainerHeight || "calc(100vh - 200px)",
            overflow: "auto",
            ...style,
          }}
        >
          {virtualizedContent}
        </div>
      );
    }

    // Using external scroll container or window scroll
    return (
      <div
        ref={useContainerMode ? containerRef : undefined}
        style={style}
      >
        {virtualizedContent}
      </div>
    );
  }

  // Non-virtualized rendering
  return (
    <div
      ref={useContainerMode ? containerRef : undefined}
      style={{
        gap: "1rem",
        display: "grid",
        gridTemplateColumns: makeArrayFromLength(cols)
          .map(() => "minmax(0, 100%)")
          .join(" "),
        ...style,
      }}
    >
      <Fragment>
        {makeArrayFromLength(cols).map((col, colI) => {
          const columnItems = data.filter((d, i) => i % cols === colI);

          return (
            <div
              key={colI}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                ...style,
              }}
            >
              {columnItems.map((d, i, ary) => {
                const isLastCard = i === ary.length - 1;
                return renderCard(cols, colI, d, i, ary, isLastCard);
              })}

              {isFetchingNextPage &&
                makeArrayFromLength(5).map((d, i) => (
                  <SkeletonLoader key={i} style={{ height: "300px", borderRadius: "0.5rem" }} />
                ))}
            </div>
          );
        })}
      </Fragment>
    </div>
  );
};
